/**
 * EarningsIntelligenceCockpit — Cockpit-language wrapper for /earnings-intelligence.
 *
 * The FIE views are deeply specialized; this shell provides a Cockpit
 * masthead + hairline tab strip and embeds the existing FIE panels.
 * No business logic changes.
 */
import { useState } from "react";
import { useRole } from "@/data/RoleContext";
import { getFIEPermissions, hasFIEAccess } from "@/data/fie/permissions";
import { FIEProvider } from "@/data/fie/context";
import { EarningsForecastDashboard } from "@/components/fie/EarningsForecastDashboard";
import { PartnerDistributionsView } from "@/components/fie/PartnerDistributionsView";
import { AssumptionsRegistryView } from "@/components/fie/AssumptionsRegistryView";
import { ReconciliationView } from "@/components/fie/ReconciliationView";
import { FIEAuditLogView } from "@/components/fie/FIEAuditLogView";
import { RoleContextBanner } from "@/components/RoleContextBanner";
import { CockpitPage, CockpitMasthead, CockpitTabs } from "@/components/cockpit/CockpitShell";
import { ShieldAlert } from "lucide-react";

export default function EarningsIntelligenceCockpit() {
  const { role } = useRole();
  const perms = getFIEPermissions(role);
  const hasAccess = hasFIEAccess(role);
  const [tab, setTab] = useState("forecast");

  if (!hasAccess || !perms) {
    return (
      <CockpitPage>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="h-12 w-12 rounded-sm border border-destructive/30 flex items-center justify-center mb-4">
            <ShieldAlert className="h-5 w-5 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight mb-2">Restricted Access</h1>
          <p className="text-sm text-muted-foreground max-w-md">
            The Financial Intelligence Engine is restricted to authorized finance and executive roles.
          </p>
        </div>
      </CockpitPage>
    );
  }

  const tabs = [
    { value: "forecast", label: "Forecast" },
    ...((perms.viewAllPartnerProjections || perms.viewOwnProjectionOnly) ? [{ value: "distributions", label: "Distributions" }] : []),
    ...((perms.editAssumptions || perms.viewCostBreakdown) ? [{ value: "assumptions", label: "Assumptions" }] : []),
    ...(perms.viewReconciliation ? [{ value: "reconciliation", label: "Reconciliation" }] : []),
    ...(perms.viewAuditLog ? [{ value: "audit", label: "Audit Log" }] : []),
  ];

  return (
    <FIEProvider>
      <CockpitPage>
        <CockpitMasthead eyebrow="Financials · Earnings" title="Earnings Intelligence" />
        <RoleContextBanner />
        <CockpitTabs tabs={tabs} value={tab} onChange={setTab} />
        <div>
          {tab === "forecast" && <EarningsForecastDashboard />}
          {tab === "distributions" && <PartnerDistributionsView />}
          {tab === "assumptions" && <AssumptionsRegistryView />}
          {tab === "reconciliation" && <ReconciliationView />}
          {tab === "audit" && <FIEAuditLogView />}
        </div>
      </CockpitPage>
    </FIEProvider>
  );
}
